import { useEffect, useId, useRef } from "react";
import type { ReactNode } from "react";
import { Button } from "@/components/ui/button";

interface ConfirmDialogProps {
  open: boolean;
  title: string;
  description?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Style destructif sur le bouton de confirmation (défaut : true, cas suppression). */
  destructive?: boolean;
  /** Spinner sur le bouton de confirmation + verrouille la fermeture pendant l'action. */
  loading?: boolean;
  /** Message d'erreur affiché sous la description (ex. échec de suppression). */
  error?: string;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * Modale de confirmation accessible et réutilisable (M3).
 * Remplace les `fixed inset-0 bg-black/50` réimplémentés à la main, qui n'avaient
 * ni `role="dialog"`/`aria-modal`, ni focus-trap, ni fermeture clavier.
 *
 * A11y : role dialog + aria-labelledby/describedby, focus piégé dans la modale,
 * Échap pour annuler, focus restauré à l'élément précédent à la fermeture.
 */
export function ConfirmDialog({
  open,
  title,
  description,
  confirmLabel = "Supprimer",
  cancelLabel = "Annuler",
  destructive = true,
  loading = false,
  error,
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  // Empêche la fermeture (backdrop/Échap) pendant qu'une action est en cours.
  const requestCancel = () => {
    if (!loading) onCancel();
  };
  const dialogRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const descId = useId();

  // Focus initial sur la modale + restauration du focus à la fermeture.
  useEffect(() => {
    if (!open) return;
    const previouslyFocused = document.activeElement as HTMLElement | null;
    dialogRef.current?.focus();
    return () => previouslyFocused?.focus?.();
  }, [open]);

  // Échap ferme ; Tab piégé à l'intérieur de la modale.
  useEffect(() => {
    if (!open) return;
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") {
        e.preventDefault();
        if (!loading) onCancel();
        return;
      }
      if (e.key !== "Tab") return;
      const focusables = dialogRef.current?.querySelectorAll<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      if (!focusables || focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, onCancel, loading]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      onClick={requestCancel}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={description ? descId : undefined}
        tabIndex={-1}
        className="bg-background rounded-lg p-6 mx-4 max-w-sm w-full shadow-lg space-y-4 outline-none"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 id={titleId} className="text-lg font-semibold">
          {title}
        </h2>
        {description && (
          <p id={descId} className="text-sm text-muted-foreground">
            {description}
          </p>
        )}
        {error && <p className="text-sm text-destructive">{error}</p>}
        <div className="flex gap-3 justify-end">
          <Button variant="outline" onClick={onCancel} disabled={loading}>
            {cancelLabel}
          </Button>
          <Button
            variant={destructive ? "destructive" : "default"}
            onClick={onConfirm}
            loading={loading}
          >
            {confirmLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}
